import React, { useState, useMemo } from 'react';
import ItemCard from './ItemCard';
import ItemModal from './ItemModal';

const Dashboard = ({ categoryTree, categories, items, generateBrandColor, onRefresh }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // 删除物品处理函数
  const handleDeleteItem = async (deletedItemId) => {
    try {
      // 调用父组件的刷新函数来重新加载数据
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('删除后刷新失败:', error);
    }
  };

  // 搜索处理函数
  const handleSearch = () => {
    // 搜索逻辑在 filteredItems 中处理
  };

  // 清空搜索
  const clearSearch = () => {
    setSearchQuery('');
  };

  // 获取所有一级分类
  const rootCategories = categoryTree;

  // 过滤逻辑：按名称或品牌搜索
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    
    const query = searchQuery.toLowerCase().trim();
    return items.filter(item => {
      const brandMatch = item.brand && item.brand.toLowerCase().includes(query);
      const nameMatch = item.name && item.name.toLowerCase().includes(query);
      return brandMatch || nameMatch;
    });
  }, [items, searchQuery]);

  // 构建过滤后的分类树
  const filteredCategoryTree = useMemo(() => {
    return rootCategories.map(rootCategory => ({
      ...rootCategory,
      items: rootCategory.items.filter(item => filteredItems.includes(item)),
      children: rootCategory.children.map(childCategory => ({
        ...childCategory,
        items: childCategory.items.filter(item => filteredItems.includes(item))
      })).filter(child => child.items.length > 0)
    })).filter(root => root.items.length > 0 || root.children.some(child => child.items.length > 0));
  }, [rootCategories, filteredItems]);

  // 添加物品
  const handleAddItem = () => {
    setSelectedItem({});
    setShowItemModal(true);
  };

  // 编辑物品
  const handleEditItem = (item) => {
    setSelectedItem(item);
    setShowItemModal(true);
  };

  // 关闭模态框
  const handleCloseModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
    onRefresh();
  };

  // 导出功能
  const handleExportCSV = () => {
    const getCategoryPath = (categoryId) => {
      if (!categoryId) return '';
      
      const category = categories.find(c => c.id === categoryId);
      if (!category) return '';
      
      if (category.parent_id) {
        const parentCategory = categories.find(c => c.id === category.parent_id);
        return `${parentCategory?.name || ''} > ${category.name}`;
      }
      
      return category.name;
    };

    const csvContent = [
      ['品牌', '名称', '分类路径', '标签', '创建时间'],
      ...filteredItems.map(item => [
        item.brand || '',
        item.name || '',
        getCategoryPath(item.category_id),
        (item.tags || []).join(';'),
        item.created_at || ''
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `物品档案_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExportJSON = () => {
    const data = {
      items: filteredItems,
      categories: categories,
      export_date: new Date().toISOString(),
      version: '3.0'
    };

    const dataStr = JSON.stringify(data, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `物品档案_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const handleImportData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const text = await file.text();
        const data = JSON.parse(text);
        
        const { db, auth } = await import('../supabaseClient');
        const user = await auth.getCurrentUser();
        
        if (!user) {
          alert('用户未登录');
          return;
        }

        let importCount = 0;
        let updateCount = 0;

        // 导入物品
        if (data.items) {
          for (const item of data.items) {
            try {
              const itemData = {
                ...item,
                user_id: user.id,
                purchase_price: item.purchase_price ? parseFloat(item.purchase_price) : null
              };
              
              // 检查是否已存在（品牌+名称）- 使用所有物品而不是过滤后的
              const existing = items.find(
                existing => existing.brand === item.brand && existing.name === item.name
              );

              if (existing) {
                // 覆盖现有数据
                await db.updateItem(existing.id, itemData);
                updateCount++;
              } else {
                // 添加新数据
                await db.addItem(itemData);
                importCount++;
              }
            } catch (error) {
              console.error('导入物品失败:', error);
            }
          }
        }

        alert(`导入完成！新增 ${importCount} 项，更新 ${updateCount} 项`);
        onRefresh();
      } catch (error) {
        console.error('导入失败:', error);
        alert('导入失败: ' + error.message);
      }
    };
    input.click();
  };

  return (
    <div className="w-full">
      {/* 顶部工具栏 */}
      <div className="rounded-xl p-4 mb-6" style={{ backgroundColor: '#18181B', border: '1px solid #2A2A2A' }}>
        <div className="flex flex-wrap items-center gap-4">
          {/* 搜索系统 */}
          <div className="flex-1 min-w-[300px]">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索好物名称或品牌..."
                className="flex-1 px-4 py-2 rounded-lg"
                style={{ 
                  backgroundColor: '#1F1F1F', 
                  border: '1px solid #2A2A2A',
                  color: '#FFFFFF'
                }}
              />
              <button
                onClick={clearSearch}
                className="px-4 py-2 rounded-lg"
                style={{ backgroundColor: '#3A3A3A', color: '#FFFFFF' }}
              >
                清空
              </button>
            </div>
          </div>

          {/* 导出导入按钮 */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleExportCSV}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors"
            >
              导出 CSV
            </button>
            <button
              onClick={handleExportJSON}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
            >
              导出 JSON
            </button>
            <button
              onClick={handleImportData}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors"
            >
              导入数据
            </button>
            <button
              onClick={handleAddItem}
              className="px-4 py-2 rounded-lg font-bold transition-colors"
              style={{ backgroundColor: '#FFFFFF', color: '#121212' }}
            >
              + 添加物品
            </button>
          </div>
        </div>
      </div>

      {/* 分组看板 */}
      <div className="overflow-x-auto">
        {(!categories || categories.length === 0) ? (
          <div className="text-center py-12">
            <div className="text-lg mb-4" style={{ color: '#A0A0A0' }}>
              暂无分类数据，请先去分类管理创建分类
            </div>
            <div className="text-sm" style={{ color: '#666666' }}>
              请点击右上角的"分类管理"按钮
            </div>
          </div>
        ) : filteredCategoryTree.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-lg mb-4" style={{ color: '#A0A0A0' }}>
              {searchQuery.trim() ? '没有找到匹配的物品' : '暂无物品数据'}
            </div>
            <button
              onClick={handleAddItem}
              className="px-6 py-3 rounded-lg"
              style={{ backgroundColor: '#FFFFFF', color: '#121212' }}
            >
              添加第一个物品
            </button>
          </div>
        ) : (
          <div className="flex space-x-8 min-w-max pb-4">
            {filteredCategoryTree.map(rootCategory => (
              <div key={rootCategory.id} className="flex-shrink-0 w-80">
                {/* 一级分类标题 */}
                <div className="rounded-t-xl p-4 mb-0" style={{ backgroundColor: '#18181B', border: '1px solid #2A2A2A', borderBottom: 'none' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-xl opacity-60"
                        style={{
                          backgroundColor: '#2D2D2D',
                          border: '1px solid #3A3A3A'
                        }}
                      >
                        {rootCategory.icon || '📦'}
                      </div>
                      <div>
                        <h3 className="font-bold" style={{ color: '#FFFFFF' }}>
                          {rootCategory.name}
                        </h3>
                        <p className="text-xs" style={{ color: '#A0A0A0' }}>
                          {rootCategory.items.length + rootCategory.children.reduce((sum, child) => sum + child.items.length, 0)} 件物品
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 二级分类和物品 */}
                <div className="rounded-b-xl p-4 space-y-4" style={{ backgroundColor: '#1F1F1F', border: '1px solid #2A2A2A', borderTop: 'none' }}>
                  {/* 一级分类下的物品 */}
                  {rootCategory.items.length > 0 && (
                    <div>
                      <div className="text-sm mb-2" style={{ color: '#A0A0A0' }}>未分类物品</div>
                      <div className="space-y-2">
                        {rootCategory.items.map(item => (
                          <ItemCard
                            key={item.id}
                            item={item}
                            generateBrandColor={generateBrandColor}
                            onEdit={handleEditItem}
                            onDelete={handleDeleteItem}
                            isCompact={true}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 二级分类 */}
                  {rootCategory.children.map(childCategory => (
                    <div key={childCategory.id}>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium" style={{ color: '#A0A0A0' }}>
                          {childCategory.icon || '📁'} {childCategory.name}
                        </span>
                        <span className="text-xs" style={{ color: '#666666' }}>
                          ({childCategory.items.length})
                        </span>
                      </div>
                      <div className="space-y-2">
                        {childCategory.items.map(item => (
                          <ItemCard
                            key={item.id}
                            item={item}
                            generateBrandColor={generateBrandColor}
                            onEdit={handleEditItem}
                            onDelete={handleDeleteItem}
                            isCompact={true}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 物品模态框 */}
      {showItemModal && (
        <ItemModal
          item={selectedItem}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default Dashboard;
